import { useLocation, useNavigate } from 'react-router-dom';
import { PanelLeft } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Avatar } from '@/components/ui/Avatar';
import { ParaconMark } from '@/components/brand/ParaconLogo';
import { homePathFor, needsProjectSelector, sidebarItemFor, sidebarNavItems } from '@/nav/navigation';
import { useAuth } from '@/features/auth/AuthContext';
import { ProjectSelector, type ProjectOption } from './ProjectSelector';

/**
 * Collapsed 72px icon rail.
 *
 * Clicking an icon navigates straight to the module. There is no hover flyout —
 * the navigation is flat, so there is no second level to reveal.
 */
export function NavRail({
  onExpand,
  userName,
  avatarUrl,
  projects,
  currentProjectId,
  onProjectChange,
}: {
  onExpand: () => void;
  userName: string;
  avatarUrl?: string | null;
  projects: ProjectOption[];
  currentProjectId: string;
  onProjectChange: (id: string) => void;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const activeItem = sidebarItemFor(location.pathname);
  const { user } = useAuth();
  // The same flat list the expanded sidebar shows, at a different density.
  const navItems = sidebarNavItems({
    feedback: Boolean(user?.feedback_access),
    executive: Boolean(user?.executive_access),
    finance: Boolean(user?.finance_access),
  });

  return (
    <aside
      className="fixed inset-y-0 left-0 z-30 flex w-rail flex-col border-r border-white/[0.06] bg-navy"
      aria-label="Primary navigation"
    >
      <div className="flex flex-col items-center gap-2 px-3 pt-4">
        <button
          type="button"
          onClick={() => navigate(homePathFor({ executive: Boolean(user?.executive_access) }))}
          aria-label="OneParacon home"
          className="rounded-lg transition-opacity hover:opacity-80"
        >
          <ParaconMark />
        </button>
        <button
          type="button"
          onClick={onExpand}
          aria-label="Expand navigation"
          className="flex h-9 w-9 items-center justify-center rounded-lg text-white/60 transition-colors hover:bg-white/10 hover:text-white"
        >
          <PanelLeft aria-hidden className="h-[18px] w-[18px]" />
        </button>
      </div>

      {needsProjectSelector(location.pathname) && (
        <div className="px-3 py-3">
          <ProjectSelector
            collapsed
            projects={projects}
            currentId={currentProjectId}
            onChange={onProjectChange}
          />
        </div>
      )}

      <nav className="flex-1 overflow-y-auto overflow-x-hidden px-3 pb-3 scrollbar-slim-dark">
        {(
          <ul className="space-y-1">
            {navItems.map((item) => {
              const active = activeItem?.id === item.id;
              const Icon = item.icon;
              return (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => navigate(item.path)}
                    aria-label={item.label}
                    aria-current={active ? 'page' : undefined}
                    title={item.label}
                    className={cn(
                      'relative flex h-11 w-11 items-center justify-center rounded-lg transition-colors',
                      active
                        ? 'bg-white/[0.14] text-white'
                        : 'text-white/60 hover:bg-white/[0.08] hover:text-white',
                    )}
                  >
                    <Icon aria-hidden className="h-[18px] w-[18px]" />
                    {active && <span aria-hidden className="absolute inset-y-2 -left-[13px] w-0.5 rounded-full bg-accent-400" />}
                    {item.attention && (
                      <span
                        aria-hidden
                        className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-gold-500"
                      />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </nav>

      <div className="flex flex-col items-center gap-3 border-t border-white/[0.07] px-3 py-4">
        <Avatar name={userName} src={avatarUrl} size="sm" />
      </div>
    </aside>
  );
}
